import { useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, Dialog, DialogActions, DialogContent, TextField } from '@mui/material';
import validator from 'validator';
import { Icon } from '../../Atoms/Icon';
import { TextBlock, TextSpan } from '../../Atoms/Text';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { ProjectContact, ProjectContactType } from '../../../store/ProjectContact';

export interface AddContactFormProps {
  assetId: string;
  projectId: string;
  handleSave: (contact: Omit<ProjectContact, 'id'>) => Promise<ProjectContact | null>;
}

export const AddContactForm = ({ assetId, projectId, handleSave }: AddContactFormProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [contactName, setContactName] = useState<string | null>(null);
  const [contactEmail, setContactEmail] = useState<string | null>(null);
  const [contactEmailError, setContactEmailError] = useState<string>('');
  const [contactPhone, setContactPhone] = useState<string | null>(null);
  const [contactCompany, setContactCompany] = useState<string | null>(null);
  const [contactType, setContactType] = useState<string | null>(null);
  const [contactTypeSearch, setContactTypeSearch] = useState<string>('');
  const [formValid, setFormValid] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleEmailUpdate = (email: string) => {
    if (!email) {
      setContactEmailError('Email is required.');
    } else {
      setContactEmailError('');
    }
    setContactEmail(email);
  };

  const validateEmail = () => {
    if (!contactEmail) {
      setContactEmailError('Email is required.');
    } else if (!validator.isEmail(contactEmail)) {
      setContactEmailError('Please enter a valid email address.');
    } else {
      setContactEmailError('');
    }
  };

  const handleCloseForm = () => {
    setContactName(null);
    setContactEmail(null);
    setContactEmailError('');
    setContactPhone(null);
    setContactCompany(null);
    setContactType(null);
    setContactTypeSearch('');
    setFormValid(false);
    setCreateError(null);
    setIsSaving(false);
    setOpen(false);
  };

  const onSaveHandler = async () => {
    if (!formValid) return;
    setIsSaving(true);
    const newContact: Omit<ProjectContact, 'id'> = {
      name: contactName as string,
      email: contactEmail as string,
      phone: contactPhone as string,
      company: contactCompany as string,
      type: contactType as string as ProjectContactType,
      projectId,
      assetId
    };
    const result = await handleSave(newContact);
    setIsSaving(false);

    if (result) {
      handleCloseForm();
    } else {
      setCreateError('Failed to create contact. Please try again.');
    }
  };

  useEffect(() => {
    if (contactName && contactEmail && !contactEmailError && contactPhone && contactType) {
      setFormValid(true);
    } else {
      setFormValid(false);
    }
  }, [contactName, contactEmail, contactPhone, contactType]);

  return (
    <>
      <button
        className="flex space-x-2 items-center"
        onClick={() => setOpen(true)}>
        <Icon type="plus" />
        <TextSpan
          color="grey12"
          size="sm">
          Add Contact
        </TextSpan>
      </button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}>
        <DialogContent>
          <div className="w-[25vw] pb-6 border-b-2">
            <TextBlock
              size="lg"
              className="my-2">
              Add Contact
            </TextBlock>
            <TextBlock className="my-2">To add a contact, enter their details below for approval.</TextBlock>
            <TextField
              label="Name"
              type="text"
              variant="outlined"
              fullWidth
              margin="normal"
              value={contactName}
              required={true}
              error={contactName !== null && !contactName}
              helperText={contactName !== null && !contactName ? 'Name is required.' : ''}
              onChange={(e) => setContactName(e.target.value)}
              InputProps={{ style: { fontSize: '.875rem', height: '2.3rem' } }}
              size={'small'}
            />
            <TextField
              label="Email"
              variant="outlined"
              type="email"
              fullWidth
              margin="normal"
              value={contactEmail}
              onChange={(e) => handleEmailUpdate(e.target.value)}
              onBlur={validateEmail}
              required={true}
              error={!!contactEmailError}
              helperText={contactEmailError}
              InputProps={{ style: { fontSize: '.875rem', height: '2.3rem' } }}
              size={'small'}
            />
            <TextField
              label="Phone"
              type="text"
              variant="outlined"
              fullWidth
              margin="normal"
              required={true}
              error={contactPhone !== null && !contactPhone}
              helperText={contactPhone !== null && !contactPhone ? 'Phone is required.' : ''}
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              InputProps={{ style: { fontSize: '.875rem', height: '2.3rem' } }}
              size={'small'}
            />
            <TextField
              label="Company"
              type="text"
              variant="outlined"
              fullWidth
              margin="normal"
              value={contactCompany}
              onChange={(e) => setContactCompany(e.target.value)}
              placeholder="Optional"
              InputProps={{ style: { fontSize: '.875rem', height: '2.3rem' } }}
              size={'small'}
            />
            <Autocomplete
              id="contact-type-selection-input"
              data-cy="contact-type"
              autoSelect={true}
              value={contactType}
              disablePortal={false}
              inputValue={contactTypeSearch}
              onInputChange={(event, newInputValue) => {
                setContactTypeSearch(newInputValue);
              }}
              onChange={(event, newValue) => setContactType(newValue)}
              onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
              options={['On Site', 'Supplier']}
              getOptionLabel={(opt) => opt}
              onBlur={() => null}
              disabled={false}
              freeSolo={false}
              size={'small'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Type of Contact"
                  InputProps={{ ...params.InputProps, style: { fontSize: '.875rem' } }}
                  variant="outlined"
                  required={true}
                  error={contactType !== null && !contactType}
                  helperText={contactType !== null && !contactType ? 'Type is required.' : ''}
                  margin="normal"
                />
              )}
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={option}>
                  {option}
                </li>
              )}
            />
          </div>
        </DialogContent>
        <DialogActions className="flex flex-col">
          {createError && (
            <div>
              <TextBlock
                size="sm14"
                color="red"
                className="mb-4">
                {createError}
              </TextBlock>
            </div>
          )}
          <div className="flex mx-4 mb-3 space-x-4 w-full justify-end">
            <button
              className={DEFAULT_CANCEL_BUTTON_STYLE}
              onClick={handleCloseForm}>
              Cancel
            </button>
            <button
              className={`${DEFAULT_ADVANCE_BUTTON_STYLE} min-w-20`}
              disabled={!formValid}
              onClick={onSaveHandler}>
              {isSaving ? (
                <CircularProgress
                  size={14}
                  color="inherit"
                />
              ) : createError ? (
                'Retry'
              ) : (
                'Save'
              )}
            </button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};
